import { useCreateMutation } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import React, { memo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Input, Button } from 'reactstrap';

export interface TenantRequestFormValues {
  businessName: string;
  contactEmail: string;
  contactPhone: string;
  region?: string;
  note?: string;
}

const TenantRequestForm: React.FC = () => {
  const [formValues, setFormValues] = useState<TenantRequestFormValues>({
    businessName: '',
    contactEmail: '',
    contactPhone: '',
    note: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { mutate: createTenantRequest, isPending } = useCreateMutation(
    API_ENDPOINTS.SAAS.TENANT_REQUEST.BASE,
  );

  const handleChange = (field: keyof TenantRequestFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = () => {
    const payload: TenantRequestFormValues = {
      businessName: formValues.businessName.trim(),
      contactPhone: formValues.contactPhone.trim(),
      contactEmail: formValues.contactEmail.trim(),
      note: formValues.note?.trim() || undefined,
    };

    setErrors({}); // Clear previous errors

    createTenantRequest(payload, {
      onSuccess: () => {
        setFormValues({
          businessName: '',
          contactEmail: '',
          contactPhone: '',
          note: '',
        });
        setErrors({});
      },
      onError: (error: any) => {
        const serverErrors = error?.response?.data?.errors;
        if (serverErrors) {
          const formattedErrors: { [key: string]: string } = {};
          Object.entries(serverErrors).forEach(([field, messages]) => {
            formattedErrors[field] = (messages as string[])[0];
          });
          setErrors(formattedErrors);
        } else {
          console.error('Unexpected error:', error);
        }
      },
    });
  };

  // Check if form is valid to enable/disable submit button
  const isFormValid = () => {
    return (
      !!formValues.businessName.trim() &&
      !!formValues.contactEmail.trim() &&
      !!formValues.contactPhone.trim()
    );
  };

  return (
    <section className="tenant-request-section py-5" id="tenant-request">
      <Container>
        <h2 className="text-center mb-5 fw-bold fs-2">Submit a Tenant Request</h2>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="p-4 shadow-sm">
              <CardBody>
                {/* Company Name */}
                <FormGroup>
                  <Label for="businessName" className="fw-bold">
                    Company Name
                  </Label>
                  <Input
                    id="businessName"
                    value={formValues.businessName}
                    onChange={(e) => handleChange('businessName', e.target.value)}
                    placeholder="Enter your company name"
                    required
                    invalid={!!errors.BusinessName}
                  />
                  {errors.BusinessName && (
                    <div className="text-danger mt-1">{errors.BusinessName}</div>
                  )}
                </FormGroup>


                {/* Contact Email */}
                <FormGroup>
                  <Label for="contactEmail" className="fw-bold">
                    Contact Email
                  </Label>
                  <Input
                    type="email"
                    id="contactEmail"
                    value={formValues.contactEmail}
                    onChange={(e) => handleChange('contactEmail', e.target.value)}
                    placeholder="Enter your email address"
                    required
                    invalid={!!errors.ContactEmail}
                  />
                  {errors.ContactEmail && (
                    <div className="text-danger mt-1">{errors.ContactEmail}</div>
                  )}
                </FormGroup>

                {/* Contact Phone */}
                <FormGroup>
                  <Label for="contactPhone" className="fw-bold">
                    Contact Phone
                  </Label>
                  <Input
                    id="contactPhone"
                    value={formValues.contactPhone}
                    onChange={(e) => handleChange('contactPhone', e.target.value)}
                    placeholder="Enter your phone number"
                    required
                    invalid={!!errors.ContactPhone}
                  />
                  {errors.ContactPhone && (
                    <div className="text-danger mt-1">{errors.ContactPhone}</div>
                  )}
                </FormGroup>

                {/* Note */}
                <FormGroup>
                  <Label for="note" className="fw-bold">
                    Additional Details (Optional)
                  </Label>
                  <Input
                    type="textarea"
                    id="note"
                    value={formValues.note || ''}
                    onChange={(e) => handleChange('note', e.target.value)}
                    placeholder="Tell us about your requirements"
                    rows={4}
                    invalid={!!errors.Note}
                  />
                  {errors.Note && <div className="text-danger mt-1">{errors.Note}</div>}
                </FormGroup>

                <div className="d-flex justify-content-end">
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isPending || !isFormValid()}
                  >
                    {isPending ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default memo(TenantRequestForm);
